/**
 * Shared HTTP helpers used by BOTH the JSON api client (`api/client.ts`) and
 * the SSE stream reader (`lib/streamReader.ts`). Lives in `lib/` so neither
 * importer inverts the layering — `api/` depends on `lib/`, never the reverse.
 */

import { COLD_START_503_MAX_DELAY_MS } from './constants';

/**
 * Parse the Retry-After response header per RFC 7231 §7.1.3. Returns the
 * server's requested delay in seconds, clamped to
 * [0, COLD_START_503_MAX_DELAY_MS/1000].
 *
 * Returns null when the header is absent or unparseable — caller treats
 * that as "no usable signal" and falls back to its own default. The
 * literal value `0` is preserved (server saying "retry immediately");
 * only an unusable signal collapses to null. The previous form
 * (`parseInt(...) || 0`) treated explicit `0` and parse-failure the same
 * way, hiding the difference.
 *
 * HTTP-date form is not supported — Cloud Run's middleware emits only
 * delta-seconds, so accepting the date form would be dead code.
 */
export function parseRetryAfter(header: string | null): number | null {
  if (header === null) return null;
  const parsed = parseInt(header, 10);
  if (!Number.isFinite(parsed)) return null;
  return Math.min(Math.max(parsed, 0), COLD_START_503_MAX_DELAY_MS / 1000);
}
